import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ArrowUpDown, MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import type { QColumnDef, QRowAction } from './types';

interface QTableGridProps<T> {
  data: T[];
  columns: QColumnDef<T>[];
  getId: (row: T) => string | number;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyIcon?: LucideIcon;

  // Sorting
  sort?: string;
  order?: 'asc' | 'desc';
  onSortChange?: (field: string, order: 'asc' | 'desc') => void;

  // Selection
  selectable?: boolean;
  selectedIds?: (string | number)[];
  onSelectionChange?: (ids: (string | number)[]) => void;

  // Row interaction
  onRowClick?: (row: T) => void;
  rowActions?: QRowAction<T>[];
}

const SKELETON_ROWS = 8;

function QTableGrid<T>({
  data,
  columns,
  getId,
  isLoading = false,
  emptyMessage = 'No hay datos para mostrar',
  emptyIcon,
  sort,
  order,
  onSortChange,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onRowClick,
  rowActions,
}: QTableGridProps<T>) {
  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleSort = React.useCallback(
    (key: string) => {
      if (!onSortChange) return;
      if (sort === key) {
        onSortChange(key, order === 'asc' ? 'desc' : 'asc');
      } else {
        onSortChange(key, 'asc');
      }
    },
    [sort, order, onSortChange]
  );

  const handleSelectAll = React.useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) return;
      onSelectionChange(checked ? data.map(getId) : []);
    },
    [data, getId, onSelectionChange]
  );

  const handleSelectRow = React.useCallback(
    (id: string | number, checked: boolean) => {
      if (!onSelectionChange) return;
      onSelectionChange(
        checked ? [...selectedIds, id] : selectedIds.filter((s) => s !== id)
      );
    },
    [selectedIds, onSelectionChange]
  );

  // Build TanStack columns from QColumnDefs
  const tanstackColumns = React.useMemo<ColumnDef<T>[]>(() => {
    const cols: ColumnDef<T>[] = [];

    if (selectable) {
      cols.push({
        id: '_selection',
        header: () => (
          <Checkbox
            checked={data.length > 0 && selectedSet.size === data.length}
            onCheckedChange={(checked) => handleSelectAll(!!checked)}
            aria-label="Seleccionar todos"
          />
        ),
        cell: ({ row }) => {
          const id = getId(row.original);
          return (
            <Checkbox
              checked={selectedSet.has(id)}
              onCheckedChange={(checked) => handleSelectRow(id, !!checked)}
              onClick={(e) => e.stopPropagation()}
              aria-label="Seleccionar fila"
            />
          );
        },
        size: 40,
      });
    }

    for (const col of columns) {
      cols.push({
        id: col.key,
        accessorFn: (row) => (row as Record<string, unknown>)[col.key],
        header: () => col.header,
        cell: ({ row }) =>
          col.render
            ? col.render(row.original)
            : String((row.original as Record<string, unknown>)[col.key] ?? ''),
        size: col.width,
        meta: { sortable: col.sortable !== false, className: col.className },
      });
    }

    if (rowActions?.length) {
      cols.push({
        id: '_actions',
        header: () => null,
        cell: ({ row }) => {
          const visibleActions = rowActions.filter(
            (a) => !a.show || a.show(row.original)
          );
          if (!visibleActions.length) return null;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Acciones</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {visibleActions.map((action) => (
                  <DropdownMenuItem
                    key={action.label}
                    className={cn(
                      action.variant === 'destructive' && 'text-destructive'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick(row.original);
                    }}
                  >
                    {action.icon && <action.icon className="mr-2 h-4 w-4" />}
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        size: 50,
      });
    }

    return cols;
  }, [columns, selectable, rowActions, data, selectedSet, getId, handleSelectAll, handleSelectRow]);

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  if (isLoading) {
    return (
      <div className="w-full overflow-auto">
        <table className="w-full caption-bottom text-sm">
          <thead className="[&_tr]:border-b">
            <tr className="border-b">
              {columns.map((col) => (
                <th key={col.key} className="h-9 px-3 py-2 text-left align-middle font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <tr key={i} className="border-b">
                {columns.map((col) => (
                  <td key={col.key} className="px-3 py-2">
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!data.length) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyMessage}
        className="py-16"
      />
    );
  }

  return (
    <div className="w-full overflow-auto">
      <table className="w-full caption-bottom text-sm">
        <thead className="[&_tr]:border-b">
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b">
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as
                  | { sortable?: boolean; className?: string }
                  | undefined;
                const isSortable = meta?.sortable !== false && header.column.id !== '_selection' && header.column.id !== '_actions';
                const isCurrentSort = sort === header.column.id;

                return (
                  <th
                    key={header.id}
                    className={cn(
                      'h-9 px-3 py-2 text-left align-middle font-medium text-muted-foreground text-xs uppercase tracking-wider',
                      header.column.id === '_selection' && 'w-10 pr-0',
                      header.column.id === '_actions' && 'w-12',
                      meta?.className
                    )}
                    style={header.column.getSize() !== 150 ? { width: header.column.getSize() } : undefined}
                  >
                    {header.isPlaceholder ? null : isSortable ? (
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-foreground transition-colors"
                        onClick={() => handleSort(header.column.id)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span className="text-muted-foreground/50">
                          {isCurrentSort && order === 'asc' ? (
                            <ArrowUp className="h-3.5 w-3.5 text-foreground" />
                          ) : isCurrentSort && order === 'desc' ? (
                            <ArrowDown className="h-3.5 w-3.5 text-foreground" />
                          ) : (
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          )}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {rows.map((row) => {
            const id = getId(row.original);
            return (
              <tr
                key={id}
                className={cn(
                  'border-b transition-colors hover:bg-muted/50',
                  selectedSet.has(id) && 'bg-muted/30',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      'px-3 py-2 align-middle',
                      cell.column.id === '_selection' && 'pr-0 w-10',
                      cell.column.id === '_actions' && 'w-12'
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export { QTableGrid };
